import API_BASE from "../config.js";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoAS from "../assets/logo-as.png";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ParticleGridBackground from "../components/ParticleGridBackground";
import Nebulosa from "../components/Nebulosa";

// Os arrays abaixo guardam so o dado estrutural (icone/numero/cor) -- o
// texto vem do i18next em tempo de render via t(`landing.features.${key}...`),
// porque t() so funciona dentro do componente (depende do idioma atual).
// Ver frontend/src/i18n/locales/{pt,en,es}.json.
const FEATURES = [
  { icon: "🛠️", key: "os" },
  { icon: "📄", key: "orcamentos" },
  { icon: "📦", key: "estoque" },
  { icon: "💰", key: "financeiro" },
  { icon: "📅", key: "agendamentos" },
  { icon: "✅", key: "checklist" },
];

const PASSOS = [
  { n: "1", key: "step1" },
  { n: "2", key: "step2" },
  { n: "3", key: "step3" },
];

const HERO_STATS = ["stat1", "stat2", "stat3"];

const MOCK_ITENS = [
  { key: "item1", cor: "#3b82f6" },
  { key: "item2", cor: "#f59e0b" },
  { key: "item3", cor: "#22c55e" },
];

const MOCK_CARDS = [
  { key: "os_abertas", valor: "12", cor: "#3b82f6" },
  { key: "receita_mes", valor: "R$ 8.4k", cor: "#22c55e" },
  { key: "estoque_baixo", valor: "3", cor: "#f59e0b" },
];

function useScrollReveal() {
  const ref = useRef(null);
  const [visivel, setVisivel] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) { setVisivel(true); obs.unobserve(el); } }); },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visivel];
}

function Estrelas({ nota, tamanho = 15 }) {
  return (
    <div style={{ display: "flex", gap: 2 }} aria-label={`${nota} de 5 estrelas`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} style={{ fontSize: tamanho, color: n <= nota ? "#fbbf24" : "rgba(255,255,255,0.18)" }}>★</span>
      ))}
    </div>
  );
}

// Carrossel de depoimentos aprovados -- fica escondido se ainda nao houver
// nenhum aprovado (nao faz sentido mostrar uma secao vazia). So exibe
// depoimentos com aprovado=true (moderacao manual do admin, ver
// services/tenant-service/src/routes/depoimentos.py).
function DepoimentosCarrossel() {
  const { t } = useTranslation();
  const [lista, setLista] = useState([]);
  const [indice, setIndice] = useState(0);

  useEffect(() => {
    fetch(API_BASE + "/api/v1/depoimentos")
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (Array.isArray(d)) setLista(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (lista.length <= 1) return;
    const iv = setInterval(() => setIndice((i) => (i + 1) % lista.length), 6500);
    return () => clearInterval(iv);
  }, [lista.length]);

  if (lista.length === 0) return null;
  const dep = lista[Math.min(indice, lista.length - 1)];

  return (
    <div style={{ maxWidth: 620, margin: "0 auto" }}>
      <div style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))",
        backdropFilter: "blur(18px) saturate(150%)", WebkitBackdropFilter: "blur(18px) saturate(150%)",
        border: "1px solid rgba(255,255,255,0.14)", borderRadius: 20, padding: "32px 36px",
        textAlign: "center", minHeight: 190,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 24px 50px -24px rgba(0,0,0,0.5)",
      }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <Estrelas nota={dep.nota} tamanho={18} />
        </div>
        <div style={{ fontSize: 15.5, lineHeight: 1.6, color: "#e2e8f0", marginBottom: 18, fontStyle: "italic" }}>
          “{dep.comentario}”
        </div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{dep.nome}</div>
        {dep.oficina && <div style={{ color: "#64748b", fontSize: 12.5, marginTop: 2 }}>{dep.oficina}</div>}
      </div>
      {lista.length > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 18 }}>
          {lista.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndice(i)}
              aria-label={`Depoimento ${i + 1}`}
              style={{
                width: i === indice ? 22 : 8, height: 8, borderRadius: 99, border: "none", cursor: "pointer",
                background: i === indice ? "#3b82f6" : "rgba(255,255,255,0.18)", transition: "all .25s",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Revelar({ children, atraso = 0, style }) {
  const [ref, visivel] = useScrollReveal();
  return (
    <div
      ref={ref}
      style={{
        ...style,
        opacity: visivel ? 1 : 0,
        transform: visivel ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.7s ease ${atraso}ms, transform 0.7s ease ${atraso}ms`,
      }}
    >
      {children}
    </div>
  );
}

// AUD-W2 (08/10/2026) -- de onde o visitante veio, descoberto sozinho.
//
// Ordem: utm_* (o mais preciso, da pra saber ate o criativo) -> fbclid, que
// o Meta acrescenta em todo clique de anuncio -> dominio do referrer ->
// "direto". Funciona hoje sem mexer no anuncio; marcando os anuncios com
// UTM depois, fica melhor sem precisar de outro deploy.
//
// NUNCA levanta erro. Esta funcao roda na renderizacao da pagina publica,
// que e o destino do trafego pago: uma excecao aqui e a landing page em
// branco. Perder a origem e um arranhao; perder a pagina e perder tudo.
const UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function detectarOrigem() {
  try {
    const partes = [];
    const p = new URLSearchParams(window.location.search || "");
    UTM.forEach((k) => {
      const v = (p.get(k) || "").trim();
      if (v) partes.push(k.replace("utm_", "") + "=" + v);
    });
    if (!partes.length && p.get("fbclid")) partes.push("source=meta");
    if (!partes.length) {
      const ref = document.referrer || "";
      if (ref) {
        try { partes.push("ref=" + new URL(ref).hostname); } catch (e) { partes.push("ref=?"); }
      } else {
        partes.push("source=direto");
      }
    }
    return partes.join(" | ").slice(0, 255);
  } catch (e) {
    return "";
  }
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Marca fixa da SAAS.GL (logo + nome). Nao vem mais do config "sistema" --
  // esse campo era editavel pelo admin e acabou sobrescrito incorretamente
  // (mostrava "A.S"). Ver auditoria de 18/08/2026.
  const sistema = { nome: "SAAS.GL", subtitulo: "Gestão de Oficinas", logoUrl: logoAS };
  const [modalAberto, setModalAberto] = useState(false);
  const [form, setForm] = useState({ nome: "", whatsapp: "", email: "", oficina: "", mensagem: "", tipo_oficina: "", forma_pagamento: "" });
  // AUD-W2 -- capturada UMA vez, na primeira renderizacao (useState com
  // funcao inicializadora). Depois disso nao muda: o que importa e de onde
  // a pessoa chegou, nao onde ela esta agora.
  const [origem] = useState(detectarOrigem);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [nomeEnviado, setNomeEnviado] = useState("");
  const [erro, setErro] = useState("");
  const [scrolled, setScrolled] = useState(false);

  // Envio de depoimento (fica pendente ate aprovacao do admin -- ver
  // DepoimentosCarrossel acima e services/tenant-service/src/routes/depoimentos.py).
  const [modalDep, setModalDep] = useState(false);
  const [formDep, setFormDep] = useState({ nome: "", oficina: "", nota: 5, comentario: "" });
  const [enviandoDep, setEnviandoDep] = useState(false);
  const [enviadoDep, setEnviadoDep] = useState(false);
  const [erroDep, setErroDep] = useState("");

  const abrirModalDep = useCallback(() => { setModalDep(true); setEnviadoDep(false); setErroDep(""); }, []);
  const fecharModalDep = useCallback(() => { setModalDep(false); }, []);

  const enviarDepoimento = async (e) => {
    e.preventDefault();
    if (!formDep.nome.trim() || !formDep.comentario.trim()) {
      setErroDep(t("landing.depoimentos.modal.erro_obrigatorio"));
      return;
    }
    setEnviandoDep(true); setErroDep("");
    try {
      const r = await fetch(API_BASE + "/api/v1/depoimentos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formDep),
      });
      if (!r.ok) throw new Error();
      setEnviadoDep(true);
      setFormDep({ nome: "", oficina: "", nota: 5, comentario: "" });
    } catch {
      setErroDep(t("landing.depoimentos.modal.erro_envio"));
    } finally {
      setEnviandoDep(false);
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const abrirModal = useCallback(() => { setModalAberto(true); setEnviado(false); setErro(""); }, []);
  const fecharModal = useCallback(() => { setModalAberto(false); }, []);

  // Regex simples so pra pegar erro de digitacao obvio (sem @, sem dominio) --
  // validacao "de verdade" (entrega/existencia do email) fica a cargo do
  // contato humano feito pelo time via WhatsApp/e-mail depois do lead.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const enviar = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.whatsapp.trim() || !form.email.trim()) {
      setErro(t("landing.modal.errors.required"));
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setErro(t("landing.modal.errors.invalid_email"));
      return;
    }
    setEnviando(true); setErro("");
    try {
      const r = await fetch(API_BASE + "/api/v1/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // AUD-W2 -- a origem viaja junto, mas fora do `form`: ela nao e
        // campo de formulario, ninguem digita e ninguem edita.
        body: JSON.stringify({ ...form, origem }),
      });
      if (!r.ok) throw new Error();
      setNomeEnviado(form.nome.trim().split(" ")[0]);
      setEnviado(true);
      // Evento de conversao (Google Ads/GTM) -- dispara so apos o lead ser
      // aceito pelo backend (r.ok), nunca antes, pra nao contar tentativas
      // que falharam no envio.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lead", lead_source: "landing_page" });
      setForm({ nome: "", whatsapp: "", email: "", oficina: "", mensagem: "", tipo_oficina: "", forma_pagamento: "" });
    } catch {
      setErro(t("landing.modal.errors.submit"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={{ background: "#0a0c12", color: "#f1f5f9", minHeight: "100vh", overflowX: "hidden", position: "relative" }}>
      {/* Nebulosa azul de fundo (5 manchas radiais, ver Nebulosa.js) --
          atrás do canvas, dá profundidade. */}
      <Nebulosa />
      {/* Fundo animado "grid + partículas" (efeito "Rede Viva", adaptado do
          mockup fusao-grid-particulas.html) -- sutil, reage ao mouse, some
          se prefers-reduced-motion. Fica atrás de tudo (zIndex 0). */}
      <ParticleGridBackground />
      <style>{`
        @keyframes lp-blob1 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(40px,-30px) scale(1.08); } 66% { transform: translate(-25px,25px) scale(0.95); } }
        @keyframes lp-blob2 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-50px,30px) scale(1.1); } }
        @keyframes lp-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }
        @keyframes lp-fadeInUp { from { opacity:0; transform: translateY(24px); } to { opacity:1; transform: translateY(0); } }
        @keyframes lp-fadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes lp-pulseRing { 0% { box-shadow: 0 0 0 0 rgba(37,99,235,0.45); } 70% { box-shadow: 0 0 0 14px rgba(37,99,235,0); } 100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); } }
        .lp-hero-title { animation: lp-fadeInUp 0.8s ease both; }
        .lp-hero-sub    { animation: lp-fadeInUp 0.8s ease 0.15s both; }
        .lp-hero-ctas   { animation: lp-fadeInUp 0.8s ease 0.3s both; }
        .lp-hero-mock   { animation: lp-fadeInUp 1s ease 0.35s both, lp-float 5s ease-in-out 1.2s infinite; }
        .lp-btn-primary { background: linear-gradient(135deg,#2563eb,#3b82f6); color:#fff; border:none; border-radius:12px; font-weight:700; font-size:15px; padding:14px 28px; cursor:pointer; transition: transform .2s, box-shadow .2s; box-shadow: 0 8px 24px -8px rgba(37,99,235,0.6); }
        .lp-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 32px -8px rgba(37,99,235,0.8); }
        .lp-btn-ghost { background: rgba(255,255,255,0.04); color:#f1f5f9; border:1px solid rgba(255,255,255,0.14); border-radius:12px; font-weight:600; font-size:14px; padding:13px 26px; cursor:pointer; transition: all .2s; }
        .lp-btn-ghost:hover { border-color:#3b82f6; background: rgba(59,130,246,0.08); }
        .lp-feature-card { background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.015)); backdrop-filter: blur(14px) saturate(150%); -webkit-backdrop-filter: blur(14px) saturate(150%); border:1px solid rgba(255,255,255,0.12); border-radius:18px; padding:28px 24px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.10), 0 20px 40px -24px rgba(0,0,0,0.5); transition: transform .25s, border-color .25s, background .25s, box-shadow .25s; }
        .lp-feature-card:hover { transform: translateY(-6px); border-color: rgba(59,130,246,0.4); background: linear-gradient(180deg, rgba(59,130,246,0.10), rgba(255,255,255,0.02)); box-shadow: inset 0 1px 0 rgba(255,255,255,0.14), 0 24px 50px -20px rgba(37,99,235,0.35); }
        .lp-nav { backdrop-filter: blur(14px); }
        .lp-pulse { animation: lp-pulseRing 2.4s infinite; }
        .lp-modal-backdrop { animation: lp-fadeIn 0.2s ease both; }
        .lp-modal-card { animation: lp-fadeInUp 0.3s ease both; }
        .lp-input { width:100%; background: rgba(255,255,255,0.05); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); border:1px solid rgba(255,255,255,0.14); border-radius:10px; color:#f1f5f9; font-size:14px; padding:11px 14px; outline:none; transition: border-color .2s, background .2s, transform .2s; box-shadow: inset 0 1px 0 rgba(255,255,255,0.06); }
        .lp-input:focus { border-color:#a78bfa; background: rgba(255,255,255,0.08); }
        .lp-input::placeholder {
          background-image: linear-gradient(90deg, #38bdf8, #a78bfa 55%, #f472b6);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: #8b93c9; opacity: 1;
        }
        .lp-btn-primary.lp-btn-float { animation: lp-float 4.5s ease-in-out infinite; }
        .lp-btn-primary.lp-btn-float:hover { animation-play-state: paused; }
        .lp-btn-neon {
          background: linear-gradient(135deg, #ec4899, #a78bfa 55%, #38bdf8);
          box-shadow: 0 10px 30px -8px rgba(236,72,153,0.55), 0 10px 30px -10px rgba(56,189,248,0.45);
        }
        .lp-btn-neon:hover { box-shadow: 0 14px 36px -8px rgba(236,72,153,0.7), 0 14px 36px -10px rgba(56,189,248,0.6); }
      `}</style>

      {/* NAV */}
      <div className="lp-nav" style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, background: scrolled ? "rgba(10,12,18,0.85)" : "transparent", borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent", transition: "all 0.3s" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Badge "bolha de vidro": backdrop-filter + reflexo (gradiente
                claro no topo) + sombra interna, igual à referência
                (fusao-grid-particulas.html). */}
            <div style={{
              position: "relative", borderRadius: "50%", height: 38, width: 38,
              display: "flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden", flexShrink: 0,
              background: "rgba(255,255,255,0.06)",
              backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "inset 0 1px 2px rgba(255,255,255,0.35), inset 0 -3px 6px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.3)",
            }}>
              <img src={sistema.logoUrl} alt="logo" style={{ height: "100%", width: "100%", objectFit: "cover" }} />
              <div aria-hidden="true" style={{
                position: "absolute", inset: 0, borderRadius: "50%", pointerEvents: "none",
                background: "linear-gradient(160deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.12) 28%, rgba(255,255,255,0) 55%)",
              }} />
            </div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>{sistema.nome}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <LanguageSwitcher />
            <button className="lp-btn-ghost" onClick={() => navigate("/login")} style={{ padding: "9px 20px", fontSize: 13 }}>{t("landing.nav.login")}</button>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section style={{ position: "relative", minHeight: "100vh", display: "flex", alignItems: "center", overflow: "hidden", paddingTop: 90 }}>
        <div style={{ position: "absolute", top: "-10%", left: "-10%", width: 480, height: 480, borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.35), transparent 70%)", filter: "blur(20px)", animation: "lp-blob1 14s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: "-15%", right: "-8%", width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, rgba(249,115,22,0.22), transparent 70%)", filter: "blur(24px)", animation: "lp-blob2 18s ease-in-out infinite" }} />

        {/* Marca d'água grande do logo, centralizada na hero inteira (maior
            e mais em evidência) -- o card acima dela recebe zIndex maior
            pra continuar legível por cima. */}
        <div aria-hidden="true" style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
          width: "min(86vw, 980px)", height: "min(86vw, 980px)", zIndex: 1,
          backgroundImage: `url(${logoAS})`, backgroundRepeat: "no-repeat",
          backgroundPosition: "center", backgroundSize: "contain",
          opacity: 0.16, pointerEvents: "none",
          WebkitMaskImage: "radial-gradient(circle, #000 0%, #000 40%, transparent 72%)",
          maskImage: "radial-gradient(circle, #000 0%, #000 40%, transparent 72%)",
        }} />

        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "40px 24px", position: "relative", zIndex: 2, display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 56, alignItems: "center" }}>
          <div>
            <div className="lp-hero-title" style={{ fontSize: "clamp(32px,4.2vw,52px)", fontWeight: 900, lineHeight: 1.12, letterSpacing: "-0.02em" }}>
              {t("landing.hero.title_line1")} <span style={{ background: "linear-gradient(135deg,#3b82f6,#60a5fa)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t("landing.hero.title_line2")}</span>
            </div>
            <div className="lp-hero-sub" style={{ color: "#94a3b8", fontSize: 17, marginTop: 22, maxWidth: 500, lineHeight: 1.6 }}>
              {t("landing.hero.subtitle")}
            </div>
            <div className="lp-hero-ctas" style={{ display: "flex", gap: 14, marginTop: 34, flexWrap: "wrap" }}>
              <button className="lp-btn-primary lp-pulse" onClick={abrirModal}>{t("landing.hero.cta_primary")}</button>
              <button className="lp-btn-ghost" onClick={() => navigate("/login")}>{t("landing.hero.cta_secondary")}</button>
            </div>
            <div style={{ display: "flex", gap: 28, marginTop: 40, flexWrap: "wrap" }}>
              {HERO_STATS.map((key) => (
                <div key={key}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9" }}>{t(`landing.hero.${key}.value`)}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>{t(`landing.hero.${key}.label`)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lp-hero-mock" style={{ position: "relative", zIndex: 3, maxWidth: 420, marginLeft: "auto", perspective: 1400 }}>
            {/* Halo azul dedicado atrás do card -- separa o mockup do resto
                do hero e reforça que ele é o elemento em destaque ("local
                estratégico": alinhado ao centro óptico do título, com um
                brilho próprio em vez de flutuar solto no fundo). */}
            <div aria-hidden="true" style={{
              position: "absolute", inset: "-14%", zIndex: -1, borderRadius: 32,
              background: "radial-gradient(60% 60% at 50% 40%, rgba(59,130,246,0.30), transparent 72%)",
              filter: "blur(30px)", pointerEvents: "none",
            }} />
            <div style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.025))",
              backdropFilter: "blur(22px) saturate(160%)", WebkitBackdropFilter: "blur(22px) saturate(160%)",
              border: "1px solid rgba(255,255,255,0.14)", borderRadius: 20, overflow: "hidden",
              boxShadow: "0 40px 90px -24px rgba(37,99,235,0.35), 0 20px 50px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.16)",
              transform: "rotateY(-6deg) rotateX(2deg)", transformStyle: "preserve-3d",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#ef4444" }} />
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#f59e0b" }} />
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#22c55e" }} />
                <div style={{ marginLeft: "auto", fontSize: 10, color: "#64748b", letterSpacing: "0.06em", textTransform: "uppercase" }}>{sistema.nome}</div>
              </div>
              <div style={{ padding: 22 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
                  {MOCK_CARDS.map((c) => (
                    <div key={c.key} style={{
                      background: "rgba(255,255,255,0.05)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                      border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12, padding: "12px 10px",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
                    }}>
                      <div style={{ fontSize: 9.5, color: "#8291a6", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t(`landing.hero.mock.${c.key}`)}</div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: c.cor }}>{c.valor}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background: "rgba(255,255,255,0.04)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "4px 10px" }}>
                  {MOCK_ITENS.map((it, i) => (
                    <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 2px", borderBottom: i < MOCK_ITENS.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: it.cor, boxShadow: `0 0 8px ${it.cor}` }} />
                      <div style={{ fontSize: 12.5, color: "#cbd5e1" }}>{t(`landing.hero.mock.${it.key}`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VIDEO DE DEMONSTRACAO -- AUD-V (08/10/2026)
          Mostrar antes de pedir: ate aqui a pagina pedia o contato da
          pessoa antes de ela ver o produto funcionando. O video fica
          logo depois do hero, que e onde o visitante vindo do anuncio
          chega quando rola a primeira tela.

          preload="metadata" e deliberado: o arquivo so e baixado quando
          a pessoa aperta play. Sem isso a pagina inteira ficaria presa
          esperando os megabytes do video no celular. */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "20px 24px 10px" }}>
        <Revelar>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: "clamp(24px,3vw,34px)", fontWeight: 800 }}>
              {t("landing.video.title", "Veja o sistema funcionando")}
            </div>
            <div style={{ color: "#94a3b8", fontSize: 15, marginTop: 10 }}>
              {t("landing.video.subtitle", "Em pouco mais de um minuto voce entende como sua oficina fica organizada.")}
            </div>
          </div>
        </Revelar>
        <Revelar atraso={80}>
          <div style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.025))",
            backdropFilter: "blur(22px) saturate(160%)", WebkitBackdropFilter: "blur(22px) saturate(160%)",
            border: "1px solid rgba(255,255,255,0.14)", borderRadius: 20, overflow: "hidden",
            boxShadow: "0 40px 90px -24px rgba(37,99,235,0.35), 0 20px 50px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.16)",
          }}>
            <video
              controls
              preload="metadata"
              playsInline
              poster={process.env.PUBLIC_URL + "/video-poster.png"}
              style={{ width: "100%", display: "block", background: "#0a0c12" }}
            >
              <source src={process.env.PUBLIC_URL + "/video-site.mp4"} type="video/mp4" />
            </video>
          </div>
        </Revelar>
        {/* O botao repete o CTA do hero de proposito: quem acabou de
            assistir esta no pico de interesse e nao deveria precisar
            rolar de volta pra cima pra agir. */}
        <div style={{ textAlign: "center", marginTop: 26 }}>
          <button className="lp-btn-primary lp-pulse" onClick={abrirModal}>
            {t("landing.hero.cta_primary")}
          </button>
        </div>
      </section>

      {/* FEATURES */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "60px 24px 40px" }}>
        <Revelar>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: "clamp(24px,3vw,34px)", fontWeight: 800 }}>{t("landing.features.title")}</div>
            <div style={{ color: "#94a3b8", fontSize: 15, marginTop: 10 }}>{t("landing.features.subtitle")}</div>
          </div>
        </Revelar>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
          {FEATURES.map((f, i) => (
            <Revelar key={f.key} atraso={i * 80}>
              <div className="lp-feature-card">
                <div style={{ fontSize: 30, marginBottom: 14 }}>{f.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{t(`landing.features.${f.key}.titulo`)}</div>
                <div style={{ color: "#94a3b8", fontSize: 13.5, lineHeight: 1.55 }}>{t(`landing.features.${f.key}.desc`)}</div>
              </div>
            </Revelar>
          ))}
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "60px 24px" }}>
        <Revelar>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: "clamp(24px,3vw,34px)", fontWeight: 800 }}>{t("landing.steps.title")}</div>
          </div>
        </Revelar>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 24 }}>
          {PASSOS.map((p, i) => (
            <Revelar key={p.key} atraso={i * 100}>
              <div style={{ textAlign: "center", padding: "0 12px" }}>
                {/* Selo numerado "de vidro" -- mesma receita do badge de logo
                    do nav (blur + reflexo + sombra interna), pra padronizar
                    com o resto da página. */}
                <div style={{
                  position: "relative", width: 48, height: 48, borderRadius: "50%",
                  background: "linear-gradient(135deg,#2563eb,#3b82f6)",
                  backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                  border: "1px solid rgba(255,255,255,0.22)", overflow: "hidden",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 800, fontSize: 18, margin: "0 auto 16px",
                  boxShadow: "inset 0 1px 2px rgba(255,255,255,0.35), inset 0 -3px 6px rgba(0,0,0,0.3), 0 8px 20px -8px rgba(37,99,235,0.55)",
                }}>
                  <span style={{ position: "relative", zIndex: 1 }}>{p.n}</span>
                  <div aria-hidden="true" style={{
                    position: "absolute", inset: 0, borderRadius: "50%", pointerEvents: "none",
                    background: "linear-gradient(160deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.12) 28%, rgba(255,255,255,0) 55%)",
                  }} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{t(`landing.steps.${p.key}.title`)}</div>
                <div style={{ color: "#94a3b8", fontSize: 13.5, lineHeight: 1.5 }}>{t(`landing.steps.${p.key}.desc`)}</div>
              </div>
            </Revelar>
          ))}
        </div>
      </section>

      {/* DEPOIMENTOS */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "20px 24px 60px" }}>
        <Revelar>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ fontSize: "clamp(24px,3vw,34px)", fontWeight: 800 }}>{t("landing.depoimentos.title")}</div>
            <div style={{ color: "#94a3b8", fontSize: 15, marginTop: 10 }}>{t("landing.depoimentos.subtitle")}</div>
          </div>
        </Revelar>
        <Revelar atraso={100}>
          <DepoimentosCarrossel />
        </Revelar>
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <button className="lp-btn-ghost" onClick={abrirModalDep} style={{ fontSize: 13, padding: "10px 22px" }}>
            {t("landing.depoimentos.cta_deixar")}
          </button>
        </div>
      </section>

      {modalDep && (
        <div className="lp-modal-backdrop" style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(5,7,12,0.72)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={fecharModalDep}>
          <div className="lp-modal-card" style={{ position: "relative", maxWidth: 460, width: "100%" }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))",
              backdropFilter: "blur(22px) saturate(160%)", WebkitBackdropFilter: "blur(22px) saturate(160%)",
              border: "1px solid rgba(255,255,255,0.14)", borderRadius: 20, overflow: "hidden",
              boxShadow: "0 40px 90px -24px rgba(37,99,235,0.30), 0 20px 50px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.14)",
              padding: 32,
            }}>
              <button onClick={fecharModalDep} style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "50%", width: 28, height: 28, color: "#94a3b8", fontSize: 16, cursor: "pointer", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>

              {!enviadoDep ? (
                <>
                  <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 6 }}>{t("landing.depoimentos.modal.title")}</div>
                  <div style={{ color: "#94a3b8", fontSize: 13.5, marginBottom: 22 }}>{t("landing.depoimentos.modal.subtitle")}</div>
                  <form onSubmit={enviarDepoimento} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input className="lp-input" placeholder={t("landing.depoimentos.modal.placeholder_nome")} value={formDep.nome} onChange={(e) => setFormDep((f) => ({ ...f, nome: e.target.value }))} />
                    <input className="lp-input" placeholder={t("landing.depoimentos.modal.placeholder_oficina")} value={formDep.oficina} onChange={(e) => setFormDep((f) => ({ ...f, oficina: e.target.value }))} />
                    <div>
                      <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6 }}>{t("landing.depoimentos.modal.label_nota")}</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} type="button" onClick={() => setFormDep((f) => ({ ...f, nota: n }))}
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 26, lineHeight: 1, color: n <= formDep.nota ? "#fbbf24" : "rgba(255,255,255,0.2)" }}>★</button>
                        ))}
                      </div>
                    </div>
                    <textarea className="lp-input" placeholder={t("landing.depoimentos.modal.placeholder_comentario")} rows={4} value={formDep.comentario} onChange={(e) => setFormDep((f) => ({ ...f, comentario: e.target.value }))} style={{ resize: "vertical" }} />
                    {erroDep && <div style={{ color: "#fca5a5", fontSize: 13 }}>{erroDep}</div>}
                    <button type="submit" className="lp-btn-primary" disabled={enviandoDep} style={{ marginTop: 6, opacity: enviandoDep ? 0.7 : 1 }}>
                      {enviandoDep ? t("landing.depoimentos.modal.enviando") : t("landing.depoimentos.modal.enviar")}
                    </button>
                  </form>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "16px 0" }}>
                  <div style={{ fontSize: 44, marginBottom: 14 }}>🙏</div>
                  <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>{t("landing.depoimentos.modal.sucesso_title")}</div>
                  <div style={{ color: "#94a3b8", fontSize: 13.5, marginBottom: 22 }}>{t("landing.depoimentos.modal.sucesso_subtitle")}</div>
                  <button className="lp-btn-ghost" onClick={fecharModalDep}>{t("landing.modal.close")}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CTA FINAL */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px 100px" }}>
        <Revelar>
          <div style={{
            background: "linear-gradient(135deg, rgba(37,99,235,0.16), rgba(249,115,22,0.08)), linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.015))",
            backdropFilter: "blur(20px) saturate(160%)", WebkitBackdropFilter: "blur(20px) saturate(160%)",
            border: "1px solid rgba(255,255,255,0.14)", borderRadius: 24, padding: "48px 32px", textAlign: "center",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14), 0 30px 70px -30px rgba(37,99,235,0.35), 0 20px 50px -20px rgba(0,0,0,0.5)",
          }}>
            <div style={{ fontSize: "clamp(22px,3vw,30px)", fontWeight: 800, marginBottom: 12 }}>{t("landing.finalCta.title")}</div>
            <div style={{ color: "#94a3b8", fontSize: 15, marginBottom: 28, maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>{t("landing.finalCta.subtitle")}</div>
            <button className="lp-btn-primary" onClick={abrirModal}>{t("landing.finalCta.button")}</button>
          </div>
        </Revelar>
      </section>

      {/* FOOTER */}
      {/* position:relative + zIndex acima do canvas (que é fixed, cobre a
          viewport inteira) + fundo sólido igual ao da página -- assim os
          pontos/linhas do fundo animado de partículas não aparecem soltos
          por cima do texto do rodapé. */}
      <footer style={{ position: "relative", zIndex: 2, background: "#0a0c12", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "28px 24px", textAlign: "center", color: "#4b5768", fontSize: 12.5 }}>
        <div>© {new Date().getFullYear()} {sistema.nome} — {t("landing.footer.rights")}</div>
        <div style={{ marginTop: 10, display: "flex", gap: 18, justifyContent: "center" }}>
          <a href="/privacidade" style={{ color: "#6b7688" }}>{t("landing.footer.privacy")}</a>
          <a href="/termos" style={{ color: "#6b7688" }}>{t("landing.footer.terms")}</a>
          <a href="/cookies" style={{ color: "#6b7688" }}>{t("landing.footer.cookies")}</a>
        </div>
      </footer>

      {/* MODAL DE INTERESSE */}
      {modalAberto && (
        <div className="lp-modal-backdrop" onClick={fecharModal} style={{ position: "fixed", inset: 0, background: "rgba(5,6,10,0.72)", backdropFilter: "blur(4px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div className="lp-modal-card" onClick={(e) => e.stopPropagation()} style={{ position: "relative", maxWidth: 440, width: "100%" }}>
            {/* Halo azul atrás do modal -- mesma receita do card do hero,
                pra manter o "vidro flutuante" consistente em toda a página. */}
            <div aria-hidden="true" style={{
              position: "absolute", inset: "-10%", zIndex: -1, borderRadius: 32,
              background: "radial-gradient(60% 60% at 50% 35%, rgba(59,130,246,0.28), transparent 72%)",
              filter: "blur(28px)", pointerEvents: "none",
            }} />
            <div style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))",
              backdropFilter: "blur(22px) saturate(160%)", WebkitBackdropFilter: "blur(22px) saturate(160%)",
              border: "1px solid rgba(255,255,255,0.14)", borderRadius: 20, overflow: "hidden",
              boxShadow: "0 40px 90px -24px rgba(37,99,235,0.30), 0 20px 50px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.14)",
              padding: 32,
            }}>
              <button onClick={fecharModal} style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "50%", width: 28, height: 28, color: "#94a3b8", fontSize: 16, cursor: "pointer", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>

              {!enviado ? (
                <>
                  <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 6 }}>{t("landing.modal.title")}</div>
                  <div style={{ color: "#94a3b8", fontSize: 13.5, marginBottom: 22 }}>{t("landing.modal.subtitle")}</div>
                  <form onSubmit={enviar} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input className="lp-input" required placeholder={t("landing.modal.placeholders.nome")} value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} />
                    <input className="lp-input" required placeholder={t("landing.modal.placeholders.whatsapp")} value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} />
                    <input className="lp-input" required type="email" placeholder={t("landing.modal.placeholders.email")} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                    <input className="lp-input" placeholder={t("landing.modal.placeholders.oficina")} value={form.oficina} onChange={(e) => setForm((f) => ({ ...f, oficina: e.target.value }))} />
                    {/* AUD-W2 -- os dois campos abaixo sao OPCIONAIS e de um
                        toque so. Nao entram na validacao, nao impedem o envio,
                        e podem continuar vazios. Select em vez de texto livre
                        de proposito: "carro", "Carro", "automovel" e "oficina
                        de carro" fariam o dado nascer impossivel de contar. */}
                    <select
                      className="lp-input"
                      value={form.tipo_oficina}
                      onChange={(e) => setForm((f) => ({ ...f, tipo_oficina: e.target.value }))}
                      style={{ color: form.tipo_oficina ? "#f1f5f9" : "#94a3b8", cursor: "pointer" }}
                    >
                      <option value="" style={{ background: "#0f1420" }}>{t("landing.modal.placeholders.tipo_oficina", "Atende carros ou motos? (opcional)")}</option>
                      <option value="carro" style={{ background: "#0f1420" }}>{t("landing.modal.tipo_oficina.carro", "Carros")}</option>
                      <option value="moto" style={{ background: "#0f1420" }}>{t("landing.modal.tipo_oficina.moto", "Motos")}</option>
                    </select>
                    <select
                      className="lp-input"
                      value={form.forma_pagamento}
                      onChange={(e) => setForm((f) => ({ ...f, forma_pagamento: e.target.value }))}
                      style={{ color: form.forma_pagamento ? "#f1f5f9" : "#94a3b8", cursor: "pointer" }}
                    >
                      <option value="" style={{ background: "#0f1420" }}>{t("landing.modal.placeholders.forma_pagamento", "Prefere pagar como? (opcional)")}</option>
                      <option value="pix" style={{ background: "#0f1420" }}>{t("landing.modal.forma_pagamento.pix", "Pix")}</option>
                      <option value="cartao" style={{ background: "#0f1420" }}>{t("landing.modal.forma_pagamento.cartao", "Cartao de credito")}</option>
                      <option value="indiferente" style={{ background: "#0f1420" }}>{t("landing.modal.forma_pagamento.indiferente", "Tanto faz")}</option>
                    </select>
                    <textarea className="lp-input" placeholder={t("landing.modal.placeholders.mensagem")} rows={3} value={form.mensagem} onChange={(e) => setForm((f) => ({ ...f, mensagem: e.target.value }))} style={{ resize: "vertical" }} />
                    {erro && <div style={{ color: "#fca5a5", fontSize: 13 }}>{erro}</div>}
                    <button type="submit" className="lp-btn-primary lp-btn-float lp-btn-neon" disabled={enviando} style={{ marginTop: 6, opacity: enviando ? 0.7 : 1 }}>
                      {enviando ? t("landing.modal.sending") : t("landing.modal.submit")}
                    </button>
                  </form>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "16px 0" }}>
                  <div style={{ fontSize: 48, marginBottom: 12, animation: "lp-pulseRing 1.8s ease-in-out 2" }}>🎉</div>
                  <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 8 }}>
                    {t("landing.modal.success.title", { nome: nomeEnviado || "" })}
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 13.5, marginBottom: 18, lineHeight: 1.5 }}>
                    {t("landing.modal.success.subtitle")}
                  </div>
                  <div style={{
                    background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)",
                    borderRadius: 14, padding: "14px 16px", marginBottom: 16, textAlign: "left",
                    display: "flex", flexDirection: "column", gap: 8,
                  }}>
                    {t("landing.modal.success.bullets", { returnObjects: true }).map((b, i) => (
                      <div key={i} style={{ fontSize: 13.5, color: "#e2e8f0" }}>{b}</div>
                    ))}
                  </div>
                  <div style={{ color: "#64748b", fontSize: 12.5, marginBottom: 22 }}>
                    {t("landing.modal.success.footer")}
                  </div>
                  <button className="lp-btn-primary lp-btn-float lp-btn-neon" onClick={fecharModal} style={{ width: "100%" }}>
                    {t("landing.modal.close")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
